import type {
  CarePlan,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
} from '@medplum/fhirtypes'
import type { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import type { SeenMedplumClient } from '@seen/core/client'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { getCarePlanCreateDefaults } from '@seen/core/models/care-plans/createDefaults'
import { CARE_PLAN_DEMOGRAPHIC_QUESTIONNAIRES } from '@seen/core/models/care-plans/demographics'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { buildTemplatedQuestionnaireResponse } from '@seen/core/models/care-plans/templates/index'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { queryTypesToString } from '@seen/core/search'
import type { QueryClient } from '@tanstack/react-query'
import { omit, orderBy } from 'lodash-es'
import { useCallback } from 'react'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

export interface CreateCarePlanParams {
  participant: Reference<Patient>
  type?: keyof typeof InitialCarePlan.TYPES
  description?: string
  onSuccess?: (carePlan: InitialCarePlan) => void
}

interface UseCreateCarePlanResult {
  createCarePlan: (params: CreateCarePlanParams) => void
  isCreatingCarePlan: boolean
}

export function useCreateCarePlan(): UseCreateCarePlanResult {
  const { mutate, isPending } = useTransactionMutation()
  const currentPractitioner = useCurrentPractitioner()
  const tagNoParticipantSummary = useFeatureFlag(
    featureFlags.carePlanNoParticipantSummaryTag
  )
  const shareDietResponse = useFeatureFlag(
    featureFlags.carePlanSharedDietResponse
  )
  const createCarePlan = useCallback(
    ({ participant, type, description, onSuccess }: CreateCarePlanParams) => {
      mutate(
        async (transaction, { queryClient, medplum }) => {
          const defaults = type ? getCarePlanCreateDefaults(type) : undefined
          const sharedDietReference = shareDietResponse
            ? await getOrCreateParticipantDietReference({
                transaction,
                queryClient,
                medplum,
                participant,
                currentPractitioner: currentPractitioner.reference,
              })
            : undefined
          const supportingInfo = defaults
            ? createNewSupportingInfo({
                transaction,
                participant,
                currentPractitioner: currentPractitioner.reference,
                questionnaireTemplates: defaults.questionnaireTemplates,
                sharedDietReference,
              })
            : await createSupportingInfo({
                transaction,
                queryClient,
                medplum,
                participant,
                currentPractitioner: currentPractitioner.reference,
                sharedDietReference,
              })

          const carePlan = new InitialCarePlan({
            subject: participant,
            contributors: [currentPractitioner.reference],
            supportingInfo,
            ...(defaults
              ? {
                  type: defaults.type,
                  title: defaults.title,
                  description: defaults.description,
                }
              : { description }),
          })
          if (type === 'SEMI_ANNUAL') {
            const currentPlan = await findCurrentActivePlan({
              queryClient,
              medplum,
              participant,
            })
            if (currentPlan?.endDate) {
              carePlan.startDate = currentPlan.endDate
            }
          }
          if (tagNoParticipantSummary) {
            carePlan.noParticipantSummary = true
          }

          transaction.create(carePlan)
        },
        onSuccess
          ? {
              onSuccess: (bundle) => {
                const carePlanResource = bundle.entry?.find(
                  (e) => e.resource?.resourceType === 'CarePlan'
                )?.resource as CarePlan
                invariant(carePlanResource, 'Care plan not found')
                const carePlan =
                  InitialCarePlan.fromFhirResource(carePlanResource)
                onSuccess(carePlan)
              },
            }
          : undefined
      )
    },
    [
      mutate,
      currentPractitioner.reference,
      tagNoParticipantSummary,
      shareDietResponse,
    ]
  )

  return { createCarePlan, isCreatingCarePlan: isPending }
}

function createNewSupportingInfo({
  participant,
  transaction,
  currentPractitioner,
  questionnaireTemplates,
  sharedDietReference,
}: {
  participant: Reference<Patient>
  transaction: TransactionMutationBuilder
  currentPractitioner: Reference<Practitioner>
  questionnaireTemplates:
    | ReturnType<typeof getCarePlanCreateDefaults>['questionnaireTemplates']
    | undefined
  sharedDietReference?: Reference<QuestionnaireResponse>
}): Reference<QuestionnaireResponse>[] {
  return CARE_PLAN_DEMOGRAPHIC_QUESTIONNAIRES.map((questionnaire) => {
    if (
      questionnaire.url === questionnaires.DietQuestionnaire.url &&
      sharedDietReference
    ) {
      return sharedDietReference
    }
    return transaction.create(
      questionnaireTemplates
        ? buildTemplatedQuestionnaireResponse({
            questionnaire,
            subject: participant,
            author: currentPractitioner,
            templates: questionnaireTemplates,
          })
        : ExtendedQuestionnaireResponse.buildFromQuestionnaire({
            questionnaire,
            author: currentPractitioner,
            status: 'in-progress',
            subject: participant,
          })
    )
  })
}

async function getOrCreateParticipantDietReference({
  queryClient,
  medplum,
  participant,
  transaction,
  currentPractitioner,
}: {
  queryClient: QueryClient
  medplum: SeenMedplumClient
  participant: Reference<Patient>
  transaction: TransactionMutationBuilder
  currentPractitioner: Reference<Practitioner>
}): Promise<Reference<QuestionnaireResponse>> {
  const query = ExtendedQuestionnaireResponse.searchQuery({
    questionnaire: questionnaires.DietQuestionnaire,
    subject: participant,
  })
  const [currentDiet] = await queryClient.fetchQuery(
    searchOptions(medplum, ExtendedQuestionnaireResponse, query)
  )
  if (currentDiet) {
    return currentDiet.reference
  }

  return transaction.create(
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.DietQuestionnaire,
      author: currentPractitioner,
      status: 'completed',
      subject: participant,
    }),
    {
      request: {
        ifNoneExist: queryTypesToString(omit(query, '_sort')),
      },
    }
  )
}

async function findCurrentActivePlan({
  queryClient,
  medplum,
  participant,
}: {
  queryClient: QueryClient
  medplum: SeenMedplumClient
  participant: Reference<Patient>
}): Promise<InitialCarePlan | undefined> {
  const activePlans = await queryClient.fetchQuery(
    searchOptions(medplum, InitialCarePlan, {
      ...InitialCarePlan.byParticipant(participant),
      status: 'active',
      _count: 20,
    })
  )
  return orderBy(
    activePlans.filter((carePlan) => carePlan.endDate),
    [(carePlan) => carePlan.endDate?.getTime() ?? 0],
    ['desc']
  )[0]
}

async function createSupportingInfo({
  queryClient,
  medplum,
  participant,
  transaction,
  currentPractitioner,
  sharedDietReference,
}: {
  queryClient: QueryClient
  medplum: SeenMedplumClient
  participant: Reference<Patient>
  transaction: TransactionMutationBuilder
  currentPractitioner: Reference<Practitioner>
  sharedDietReference?: Reference<QuestionnaireResponse>
}): Promise<Reference<QuestionnaireResponse>[]> {
  return Promise.all([
    createDraftQuestionnaireResponse({
      questionnaire: questionnaires.EmergencyContactAndDecisionsQuestionnaire,
      queryClient,
      medplum,
      participant,
      transaction,
      currentPractitioner,
    }),
    createDraftQuestionnaireResponse({
      questionnaire: questionnaires.PatientEvaluationQuestionnaire,
      queryClient,
      medplum,
      participant,
      transaction,
      currentPractitioner,
    }),
    createDraftQuestionnaireResponse({
      questionnaire: questionnaires.PatientPhysicalFunctioningQuestionnaire,
      queryClient,
      medplum,
      participant,
      transaction,
      currentPractitioner,
    }),
    sharedDietReference ??
      createDraftQuestionnaireResponse({
        questionnaire: questionnaires.DietQuestionnaire,
        queryClient,
        medplum,
        participant,
        transaction,
        currentPractitioner,
      }),
    createDraftQuestionnaireResponse({
      questionnaire: questionnaires.ServiceAllocationQuestionnaire,
      queryClient,
      medplum,
      participant,
      transaction,
      currentPractitioner,
    }),
  ])
}

async function createDraftQuestionnaireResponse({
  queryClient,
  medplum,
  participant,
  transaction,
  currentPractitioner,
  questionnaire,
}: {
  queryClient: QueryClient
  medplum: SeenMedplumClient
  participant: Reference<Patient>
  transaction: TransactionMutationBuilder
  currentPractitioner: Reference<Practitioner>
  questionnaire: Questionnaire
}): Promise<Reference<QuestionnaireResponse>> {
  const [currentQR] = await queryClient.fetchQuery(
    searchOptions(
      medplum,
      ExtendedQuestionnaireResponse,
      ExtendedQuestionnaireResponse.searchQuery({
        questionnaire,
        subject: participant,
      })
    )
  )

  const draftQR = transaction.create(
    currentQR
      ? ExtendedQuestionnaireResponse.buildFromQuestionnaireResponse({
          questionnaireResponse: currentQR,
          author: currentPractitioner,
          status: 'in-progress',
        })
      : ExtendedQuestionnaireResponse.buildFromQuestionnaire({
          questionnaire,
          author: currentPractitioner,
          status: 'in-progress',
          subject: participant,
        })
  )

  return draftQR
}
